import { addDays, startOfCalendarWeek } from '~/calculations/calendar'
import type { DailyCheckRecord } from '~/models/dailyCheck'
import type { FitnessDomain } from '~/models/fitnessDomain'
import { requiresDailyCheck } from '~/models/nutritionPlan'
import { projectHasStarted } from '~/models/project'
import {
  countTrainingByDomain,
  type TrainingSessionCounts,
  type TrainingWeekRecord,
} from '~/models/trainingWeek'
import type { WeekChecklist } from '~/models/weekChecklist'
import { buildWeekChecklist } from '~/models/weekChecklist'
import { DEFAULT_RULES_THRESHOLDS } from '~/rules/thresholds'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { listDailyChecksInRange } from '~/storage/repositories/dailyCheckRepository'
import { listParticipatingDomainGoals } from '~/storage/repositories/domainGoalRepository'
import { listMeasurementSessions } from '~/storage/repositories/measurementRepository'
import { getActivePhase } from '~/storage/repositories/phaseRepository'
import { getActiveProject } from '~/storage/repositories/projectRepository'
import { listWeeklyReviewsForWeek } from '~/storage/repositories/weeklyReviewRepository'
import { listWeightEntriesInRange } from '~/storage/repositories/weightRepository'
import { type CalendarDate, calendarDateFrom, unwrapDate } from '~/types/dates'
import type { Result } from '~/utils/result'
import { ok } from '~/utils/result'
import { getActiveNutritionPlan, isWeekFinalized } from './dailyCheckService'
import { getCurrentTrainingWeek } from './trainingWeekService'
import { getEffectiveWeekStartDay } from './weekBoundaryService'

/**
 * Gathering what the week actually contains so far (D-099 §D).
 *
 * The deciding is in `models/weekChecklist.ts` and is pure; this is the six
 * reads that feed it. **Nothing here counts anything a existing module already
 * counts** — the weigh-in threshold is D-036's, the daily answers go through the
 * daily-check model's own notion of an answer, and whether a week is finalized
 * is `isWeekFinalized`, which the correction boundary already depends on.
 *
 * **One service call, so Today's `Promise.all` grows by one rather than six.**
 */
export async function getWeekChecklist(
  db: SolaDatabase,
  now: Date,
): Promise<Result<WeekChecklist, StorageError>> {
  const weekStartDay = await getEffectiveWeekStartDay(db, now)
  if (!weekStartDay.ok) return weekStartDay

  const today = calendarDateFrom(now)
  const weekStart = startOfCalendarWeek(today, weekStartDay.value)
  const weekEnd = addDays(weekStart, 6)

  const project = await getActiveProject(db)
  if (!project.ok) return project

  /* A project that has not begun has no week (D-107 §4).
     The return is BEFORE the `Promise.all`, not a filter after it, because
     `getCurrentTrainingWeek` inside it materialises the week as a side effect of
     being asked. Suppressing the result would still have written the record. */
  if (project.value !== undefined && !projectHasStarted(project.value, today)) {
    return ok(emptyChecklist(weekStart))
  }

  const [goals, phase, weights, sessions, reviews, dailyChecks, nutritionPlan, trainingWeek] =
    await Promise.all([
      project.value === undefined
        ? Promise.resolve(ok([] as never[]))
        : listParticipatingDomainGoals(db, project.value.id),
      getActivePhase(db),
      listWeightEntriesInRange(db, weekStart, today),
      listMeasurementSessions(db),
      listWeeklyReviewsForWeek(db, weekStart),
      listDailyChecksInRange(db, unwrapDate(weekStart), unwrapDate(weekEnd)),
      getActiveNutritionPlan(db),
      /* Materialises the week if this is the first read of it, which is exactly
         what D-105 §6 means by lazy: Today asking what the week contains is the
         moment the week comes into existence. */
      getCurrentTrainingWeek(db, now),
    ])
  if (!goals.ok) return goals
  if (!phase.ok) return phase
  if (!weights.ok) return weights
  if (!sessions.ok) return sessions
  if (!reviews.ok) return reviews
  if (!dailyChecks.ok) return dailyChecks
  if (!nutritionPlan.ok) return nutritionPlan
  if (!trainingWeek.ok) return trainingWeek

  const plan = nutritionPlan.value

  return ok(
    buildWeekChecklist({
      weekStartDate: weekStart,
      goals: goals.value,
      bodyCompositionPhaseActive: phase.value !== undefined,
      nutritionDailyChecksRequired: plan !== undefined && requiresDailyCheck(plan),
      counts: {
        weighIns: weights.value.length,
        measurementSessions: sessions.value.filter(
          (session) =>
            unwrapDate(session.date) >= unwrapDate(weekStart) &&
            unwrapDate(session.date) <= unwrapDate(today),
        ).length,
        nutritionDaysAnswered: countAnsweredNutritionDays(dailyChecks.value),
        reviewFinalized: isWeekFinalized(reviews.value),
        trainingByDomain: trainingByDomain(trainingWeek.value),
      },
      thresholds: {
        weighInsRequired: DEFAULT_RULES_THRESHOLDS.sufficiency.sufficientMinEntries,
      },
    }),
  )
}

/**
 * The week a waiting project contains: nothing (D-107 §4).
 *
 * Built through `buildWeekChecklist` rather than hand-written, so the empty
 * shape is whatever that model says it is and cannot drift from it.
 */
function emptyChecklist(weekStart: CalendarDate): WeekChecklist {
  return buildWeekChecklist({
    weekStartDate: weekStart,
    goals: [],
    bodyCompositionPhaseActive: false,
    nutritionDailyChecksRequired: false,
    counts: {
      weighIns: 0,
      measurementSessions: 0,
      nutritionDaysAnswered: 0,
      reviewFinalized: false,
      trainingByDomain: {},
    },
    thresholds: {
      weighInsRequired: DEFAULT_RULES_THRESHOLDS.sufficiency.sufficientMinEntries,
    },
  })
}

/**
 * Days with at least one judgeable nutrition answer.
 *
 * **`unsure` and an unanswered day are both missing evidence, never a failure**
 * (D-096), so neither is counted — which is why this asks for `yes` or `no`
 * rather than for the field being present.
 */
function countAnsweredNutritionDays(checks: readonly DailyCheckRecord[]): number {
  return checks.filter(
    (check) =>
      check.nutrition !== undefined &&
      Object.values(check.nutrition).some((answer) => answer === 'yes' || answer === 'no'),
  ).length
}

/**
 * Sessions recorded against a domain this week.
 *
 * **This is the line that was structurally frozen, and this is where it thaws.**
 * From C6.0 until C7.5 it counted `dailyChecks.sessionDone`, a field nothing
 * ever wrote, so every commitment line read *"0 recorded, 3 planned — Not yet"*
 * permanently (D-105 §1). The cause was never the field: a week was a quota, so
 * there was no *Monday's Pull day* for anyone to mark done. The materialised
 * week is what makes the count answerable, and reading it is all this does.
 *
 * **`undefined` when there is no materialised week**, so the checklist can fall
 * back to describing the plan rather than reporting a zero it has no basis for.
 */
function trainingByDomain(
  week: TrainingWeekRecord | undefined,
): Readonly<Partial<Record<FitnessDomain, TrainingSessionCounts>>> {
  return week === undefined ? {} : countTrainingByDomain(week)
}
